import IconButton from "../kit/IconButton";
import React, { useState, useRef, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import { FaUser } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import axiosInstance from "../../api/axiosInstance";
import { debounce } from "lodash";
import Loader from "../icons/Loader";
import "./ArtistSelector.css";

const ArtistSelector = ({ 
  selectedArtists = [], 
  onChange, 
  maxArtists = 10,
  label = "tag artists"
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [scrollIndicators, setScrollIndicators] = useState({ left: false, right: false });
  
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useRef(
    debounce(async (query) => {
      if (!query || query.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await axiosInstance.get("/events/search", {
          params: {
            search: query,
            type: "account",
            accountType: "artist,business",
            size: 10
          }
        });

        console.log("Artist search response:", data);

        // API returns data.data not data.results
        const results = data.data || [];
        
        // Filter out already selected artists
        const selectedIds = selectedArtists.map(a => a._id);
        const filtered = results.filter(
          result => !selectedIds.includes(result._id)
        );
        
        console.log("Filtered artists:", filtered);
        console.log("Setting showDropdown to true, filtered length:", filtered.length);
        
        setSearchResults(filtered);
        setShowDropdown(true); // Always show dropdown when search completes
      } catch (error) {
        console.error("Error searching artists:", error);
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300)
  ).current;

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Add artist to selection
  const handleAddArtist = (artist) => {
    if (selectedArtists.length >= maxArtists) {
      return;
    }

    const newSelection = [...selectedArtists, artist];
    onChange(newSelection);
    
    // Clear search
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    searchInputRef.current?.focus();
  };

  // Remove artist from selection
  const handleRemoveArtist = (artistId) => {
    const newSelection = selectedArtists.filter(a => a._id !== artistId);
    onChange(newSelection);
  };

  // Update scroll indicators
  const updateScrollIndicators = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setScrollIndicators({
      left: scrollLeft > 0,
      right: scrollLeft < scrollWidth - clientWidth - 5
    });
  };

  // Check scroll on mount and when artists change
  useEffect(() => {
    updateScrollIndicators();
  }, [selectedArtists]);

  // Debug: Log state changes
  useEffect(() => {
    console.log("State update:", {
      showDropdown,
      isSearching,
      resultsCount: searchResults.length,
      searchQuery
    });
  }, [showDropdown, isSearching, searchResults, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display name for artist
  const getArtistName = (artist) => {
    if ((artist.accountType === "business" || artist.accountType === "artist") && artist.businessName) {
      return artist.businessName;
    }
    return `${artist.firstName || ""} ${artist.lastName || ""}`.trim();
  };

  return (
    <div className="artist-selector">
      {/* Label */}
      <div className="artist-selector-header">
        <span className="ui-label">{label}</span>
        <span className="artist-selector-count inyc-counter">
          {selectedArtists.length}/{maxArtists}
        </span>
      </div>

      {/* Search Input */}
      <div className="artist-selector-search-container">
        <div className="ui-input-wrapper">
          <FiSearch className="ui-input-icon" size={16} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="search artists and businesses..."
            className="ui-input"
            disabled={selectedArtists.length >= maxArtists}
          />
          {searchQuery && (
            <IconButton
              label="clear search"
              size="xs"
              icon={<IoClose />}
              onClick={() => {
                setSearchQuery("");
                setSearchResults([]);
                setShowDropdown(false);
              }}
            />
          )}
        </div>

        {/* Search Results Dropdown */}
        {!isSearching && showDropdown && searchResults.length > 0 && (
          <div ref={dropdownRef} className="inyc-menu artist-selector-dropdown">
            {searchResults.map((artist) => (
              <div
                key={artist._id}
                onClick={() => handleAddArtist(artist)}
                className="inyc-menu-item artist-selector-dropdown-item"
              >
                <div className="artist-selector-dropdown-avatar">
                  {artist.profilePicture ? (
                    <img
                      src={artist.profilePicture}
                      alt={getArtistName(artist)}
                      className="artist-selector-dropdown-avatar-img"
                    />
                  ) : (
                    <FaUser size={14} color="var(--inyc-primary-color)" />
                  )}
                </div>
                <div className="artist-selector-dropdown-info">
                  <div className="artist-selector-dropdown-name">
                    {getArtistName(artist)}
                  </div>
                  <div className="artist-selector-dropdown-type">
                    {artist.accountType === "artist" ? "artist" : "business"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading state */}
        {isSearching && searchQuery && (
          <div className="inyc-menu artist-selector-dropdown">
            <div className="artist-selector-dropdown-loading">
              <Loader height={8} />
            </div>
          </div>
        )}

        {/* No results */}
        {!isSearching && showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && (
          <div className="inyc-menu artist-selector-dropdown">
            <div className="artist-selector-dropdown-empty">
              No artists or businesses found
            </div>
          </div>
        )}
      </div>

      {/* Selected Artists */}
      {selectedArtists.length > 0 && (
        <div className="artist-selector-selected-wrapper">
          <div
            ref={scrollContainerRef}
            className="artist-selector-selected-container"
            onScroll={updateScrollIndicators}
          >
            {selectedArtists.map((artist) => (
              <span key={artist._id} className="inyc-tag has-avatar">
                <span className="inyc-tag-avatar">
                  {artist.profilePicture ? (
                    <img
                      src={artist.profilePicture}
                      alt={getArtistName(artist)}
                      className="artist-selector-chip-avatar-img"
                    />
                  ) : (
                    <FaUser size={12} className="text-fg-muted" />
                  )}
                </span>
                {getArtistName(artist)}
                <IconButton label={`remove ${getArtistName(artist)}`} size="xs" icon={<IoClose />} onClick={() => handleRemoveArtist(artist._id)} />
              </span>
            ))}
          </div>
          
          {/* Fade indicators */}
          {scrollIndicators.left && <div className="artist-selector-fade-left" />}
          {scrollIndicators.right && <div className="artist-selector-fade-right" />}
        </div>
      )}

      {/* Max artists warning */}
      {selectedArtists.length >= maxArtists && (
        <div className="inyc-panel is-sm is-warning text-center text-kit-2xs">
          Maximum {maxArtists} artists reached
        </div>
      )}
    </div>
  );
};

export default ArtistSelector;

